/* ============================================================
   LUMA Theme JS — cart drawer, AJAX cart, reveal, sticky header
   ============================================================ */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const money = (cents, format) => {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) return Shopify.formatMoney(cents, format);
    return '$' + (cents / 100).toFixed(2);
  };

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const els = $$('.reveal');
    if (!('IntersectionObserver' in window) || els.length === 0) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Sticky header ---------- */
  function initStickyHeader() {
    const header = $('.header');
    if (!header) return;
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('header--scrolled', y > 10);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Mobile nav ---------- */
  function initMobileNav() {
    const openBtn = $('[data-mobile-nav-open]');
    const nav = $('#MobileNav');
    if (!openBtn || !nav) return;
    const close = () => nav.removeAttribute('open');
    openBtn.addEventListener('click', () => nav.setAttribute('open', ''));
    $$('[data-mobile-nav-close]', nav).forEach((b) => b.addEventListener('click', close));
    $('.mobile-nav__overlay', nav)?.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* ---------- Search drawer ---------- */
  function initSearchDrawer() {
    const openBtn = $('[data-search-open]');
    const drawer = $('#SearchDrawer');
    if (!openBtn || !drawer) return;
    const input = $('input[type="search"]', drawer);
    const open = () => { drawer.setAttribute('open', ''); setTimeout(() => input?.focus(), 100); };
    const close = () => drawer.removeAttribute('open');
    openBtn.addEventListener('click', open);
    $$('[data-search-close]', drawer).forEach((b) => b.addEventListener('click', close));
    $('.search-drawer__overlay', drawer)?.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    // Predictive search
    let timer;
    if (input) {
      input.addEventListener('input', (e) => {
        clearTimeout(timer);
        const q = e.target.value.trim();
        const results = $('#PredictiveResults', drawer);
        if (!results) return;
        if (q.length < 2) { results.innerHTML = ''; return; }
        timer = setTimeout(async () => {
          try {
            const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`;
            const res = await fetch(url);
            const text = await res.text();
            const doc = new DOMParser().parseFromString(text, 'text/html');
            results.innerHTML = doc.body.innerHTML;
          } catch (err) { /* silent */ }
        }, 250);
      });
    }
  }

  /* ---------- Cart drawer ---------- */
  function getCartState() {
    return fetch(`${window.routes.cart_url}?view=drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return {
          count: parseInt(doc.querySelector('[data-cart-count]')?.dataset.cartCount || '0', 10),
          subtotal: doc.querySelector('[data-cart-subtotal]')?.dataset.cartSubtotal || '0',
          freeShipping: doc.querySelector('[data-free-shipping]')?.outerHTML || '',
          items: doc.querySelector('[data-cart-items]')?.innerHTML || '',
          upsells: doc.querySelector('[data-cart-upsells]')?.innerHTML || '',
          empty: !!doc.querySelector('[data-cart-empty]'),
        };
      });
  }

  function updateCartUI(state) {
    const drawer = $('#CartDrawer');
    if (!drawer) return;
    const countEls = $$('[data-cart-count-display]');
    countEls.forEach((el) => {
      el.textContent = state.count;
      el.style.display = state.count > 0 ? '' : 'none';
    });
    const itemsContainer = $('[data-cart-items]', drawer);
    if (itemsContainer) itemsContainer.innerHTML = state.items;
    const upsellsContainer = $('[data-cart-upsells]', drawer);
    if (upsellsContainer) upsellsContainer.innerHTML = state.upsells;
    const freeShippingEl = $('[data-free-shipping]', drawer);
    if (freeShippingEl) freeShippingEl.outerHTML = state.freeShipping;
    const subtotalEl = $('[data-cart-subtotal-display]', drawer);
    if (subtotalEl) subtotalEl.textContent = money(state.subtotal, window.moneyFormat);
    const emptyEl = $('[data-cart-empty]', drawer);
    const contentEl = $('[data-cart-content]', drawer);
    if (emptyEl && contentEl) {
      emptyEl.style.display = state.empty ? '' : 'none';
      contentEl.style.display = state.empty ? 'none' : '';
    }
    bindCartEvents();
  }

  function openCart() {
    const drawer = $('#CartDrawer');
    if (drawer) {
      drawer.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
    }
  }
  function closeCart() {
    const drawer = $('#CartDrawer');
    if (drawer) {
      drawer.removeAttribute('open');
      document.body.style.overflow = '';
    }
  }

  function addToCart(form) {
    const formData = new FormData(form);
    fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => r.json())
      .then(() => getCartState().then(updateCartUI).then(openCart))
      .then(() => showToast('Added to cart'))
      .catch(() => showToast(window.cartStrings?.error || 'Error', true));
  }

  function changeCart(line, quantity) {
    fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ line, quantity }),
    })
      .then((r) => r.json())
      .then(() => getCartState().then(updateCartUI))
      .catch(() => showToast(window.cartStrings?.error || 'Error', true));
  }

  function bindCartEvents() {
    $$('[data-cart-add-form]').forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      form.addEventListener('submit', (e) => { e.preventDefault(); addToCart(form); });
    });
    $$('[data-quick-add]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.dataset.quickAdd;
        fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] }),
        })
          .then((r) => r.json())
          .then(() => getCartState().then(updateCartUI).then(openCart))
          .then(() => showToast('Added to cart'))
          .catch(() => showToast(window.cartStrings?.error || 'Error', true));
      });
    });
    $$('[data-cart-change]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.cartChange, 10);
        const qtyEl = $(`[data-cart-qty="${line}"]`);
        const current = parseInt(qtyEl?.textContent || '1', 10);
        const delta = btn.dataset.action === 'inc' ? 1 : -1;
        changeCart(line, Math.max(0, current + delta));
      });
    });
    $$('[data-cart-remove]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => changeCart(parseInt(btn.dataset.cartRemove, 10), 0));
    });
    $$('[data-cart-open]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', (e) => { e.preventDefault(); openCart(); });
    });
    $$('[data-cart-close]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', closeCart);
    });
    $('.cart-drawer__overlay')?.addEventListener('click', closeCart);
  }

  /* ---------- Product page: variant + qty + gallery ---------- */
  function initProduct() {
    const form = $('[data-product-form]');
    if (!form) return;

    // Variant selector (radio buttons / swatches)
    $$('[data-variant-input]', form).forEach((input) => {
      input.addEventListener('change', () => {
        const selected = $$('[data-variant-input]:checked', form).map((i) => i.value);
        const variantData = JSON.parse($('#VariantJson')?.textContent || '[]');
        const matched = variantData.find((v) => selected.every((val, i) => v.options[i] === val));
        const idInput = $('[name="id"]', form);
        if (matched && idInput) {
          idInput.value = matched.id;
          // Update price
          const priceEl = $('[data-product-price]');
          if (priceEl) priceEl.textContent = money(matched.price, window.moneyFormat);
          const compareEl = $('[data-product-compare]');
          if (compareEl) {
            if (matched.compare_at_price > matched.price) {
              compareEl.textContent = money(matched.compare_at_price, window.moneyFormat);
              compareEl.style.display = '';
            } else compareEl.style.display = 'none';
          }
          // Update button
          const btn = $('[data-add-to-cart]', form);
          if (btn) {
            if (!matched.available) { btn.textContent = window.productStrings?.soldOut || 'Sold Out'; btn.disabled = true; }
            else { btn.textContent = window.productStrings?.addToCart || 'Add to Cart'; btn.disabled = false; }
          }
        }
      });
    });

    // Quantity
    const qtyEl = $('[data-product-qty]', form);
    $$('[data-qty-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const current = parseInt(qtyEl.textContent || '1', 10);
        const next = Math.max(1, current + (btn.dataset.action === 'inc' ? 1 : -1));
        qtyEl.textContent = next;
        const hiddenQty = $('[name="quantity"]', form);
        if (hiddenQty) hiddenQty.value = next;
      });
    });

    // Gallery
    $$('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const main = $('[data-gallery-main]');
        if (main && thumb.dataset.galleryThumb) {
          main.src = thumb.dataset.galleryThumb;
          main.alt = thumb.alt;
          $$('[data-gallery-thumb]').forEach((t) => t.removeAttribute('aria-current'));
          thumb.setAttribute('aria-current', 'true');
        }
      });
    });

    // Sticky ATC
    const stickyBar = $('#StickyAtc');
    if (stickyBar) {
      const mainBtn = $('[data-add-to-cart]', form);
      const observer = new IntersectionObserver(
        ([entry]) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      );
      if (mainBtn) observer.observe(mainBtn);
    }
  }

  /* ---------- Exit intent popup ---------- */
  function initExitIntent() {
    const popup = $('#ExitIntent');
    if (!popup) return;
    if (sessionStorage.getItem('exitIntentSeen')) { popup.remove(); return; }
    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      popup.setAttribute('open', '');
      sessionStorage.setItem('exitIntentSeen', '1');
    };
    document.addEventListener('mouseout', (e) => { if (e.clientY <= 0) show(); });
    setTimeout(show, 25000);
    $$('[data-exit-close]', popup).forEach((b) => b.addEventListener('click', () => popup.removeAttribute('open')));
    $('.exit-intent__overlay', popup)?.addEventListener('click', () => popup.removeAttribute('open'));
  }

  /* ---------- Toast ---------- */
  function showToast(msg, isError) {
    let toast = $('#Toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'Toast';
      toast.className = 'toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.style.background = isError ? 'var(--color-error)' : 'var(--color-espresso)';
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2500);
  }

  /* ---------- Marquee duplication ---------- */
  function initMarquee() {
    $$('.marquee__track').forEach((track) => {
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.parentElement.appendChild(clone);
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initStickyHeader();
    initMobileNav();
    initSearchDrawer();
    bindCartEvents();
    initProduct();
    initExitIntent();
    initMarquee();
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCart(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on section reload (Shopify theme editor)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
